// NGX Json Viewer
import { NgxJsonViewerModule } from 'ngx-json-viewer';
// NGX Mask
import { NgxMaskDirective } from 'ngx-mask';
import { QuillModule } from 'ngx-quill';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

// Cdk Material
import { CdkAccordionModule } from '@angular/cdk/accordion';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
// Angular
import {
  CommonModule,
  DatePipe,
  DecimalPipe,
  NgOptimizedImage,
  TitleCasePipe,
} from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
} from '@angular/material-moment-adapter';
// Material
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';

// Components
import {
  ActionButtonComponent,
  ActivityLogComponent,
  AutocompleteComponent,
  BreadcrumbsComponent,
  ChipsComponent,
  ClientAutocompleteComponent,
  ClientListAutocompleteComponent,
  ConversationMiniatureComponent,
  DateControlComponent,
  DateRangeComponent,
  DatetimeInputComponent,
  DatetimeInputDialogComponent,
  DialogComponent,
  DialogFormComponent,
  DialogInfoComponent,
  DownloadsManagementComponent,
  EditorComponent,
  ExpandablePanelComponent,
  FloatButtonComponent,
  FormErrorsComponent,
  GalleryViewerComponent,
  ImgActionsComponent,
  ImgViewerComponent,
  InfoHelperComponent,
  JsonHighlightComponent,
  JsonRecordComponent,
  JsonViewerComponent,
  ListItemTemplateComponent,
  LongNumberControlComponent,
  MailingListAutocompleteComponent,
  MiniatureComponent,
  MoneyControlComponent,
  NoDataComponent,
  OSQueryFieldComponent,
  ProcessLogComponent,
  ScrollToTopComponent,
  ScrollableTitleComponent,
  SearchboxComponent,
  ServerSideAutocompleteComponent,
  SidenavComponent,
  SimpleTableComponent,
  SimpleZoomComponent,
  SmallMiniatureComponent,
  SnackbarComponent,
  SourcesAutocompleteComponent,
  SplitPaneComponent,
  TimeFrameComponent,
  UnitNanosControlComponent,
  UploadFileComponent,
} from './components';
import { ButtonToggleComponent } from './components/button-toggle/button-toggle.component';
import { CommentsListComponent } from './components/comments-list/comments-list.component';
import { CommentsComponent } from './components/comments/comments.component';
import { ExpandableButtonComponent } from './components/expandable-button/expandable-button.component';
import { ImageGalleryComponent } from './components/image-gallery/image-gallery.component';
import { PhysicalAddressComponent } from './components/physical-address/physical-address.component';
import { ReadOnlyBannerComponent } from './components/read-only-banner/read-only-banner.component';
import { SecondarySideButtonsComponent } from './components/side-buttons/secondary-side-buttons/secondary-side-buttons.component';
import { SideButtonsComponent } from './components/side-buttons/side-buttons.component';
import { TranslationSwitchComponent } from './components/translation-switch/translation-switch.component';
import { UploadImageComponent } from './components/upload-image/upload-image.component';
import { ValidateTAInputComponent } from './components/validate-ta-input/validate-ta-input.component';
import { MonthYearFormatDirective } from './directives/month-year-format.directive';
import {
  CaseTransformPipe,
  ContainInArrayPipe,
  CustomDatePipe,
  DateDifferencePipe,
  FormErrorPipe,
  HandleFunctionPipe,
  JsonHighlightPipe,
  PermissionsPipe,
  SpacesPipe,
  TimeAgoPipe,
} from './pipes';

const angularModules = [
  CommonModule,
  NgOptimizedImage,
  RouterModule,
  FormsModule,
  ReactiveFormsModule,
];

const materialModules = [
  MatButtonModule,
  MatProgressSpinnerModule,
  MatProgressBarModule,
  MatMenuModule,
  MatMenuModule,
  MatToolbarModule,
  MatListModule,
  MatIconModule,
  MatFormFieldModule,
  MatInputModule,
  MatAutocompleteModule,
  MatDialogModule,
  MatExpansionModule,
  MatPaginatorModule,
  MatTableModule,
  MatSidenavModule,
  MatExpansionModule,
  MatSortModule,
  MatTooltipModule,
  MatCheckboxModule,
  MatCardModule,
  MatSnackBarModule,
  ClipboardModule,
  MatTabsModule,
  MatChipsModule,
  ScrollingModule,
  MatSelectModule,
  MatSlideToggleModule,
  MatButtonToggleModule,
  MatDatepickerModule,
  DragDropModule,
  MatGridListModule,
  MatSliderModule,
  MatStepperModule,
  MatRadioModule,
];

const cdkModules = [CdkAccordionModule, ClipboardModule];

@NgModule({
  imports: [
    ...angularModules,
    ...materialModules,
    ...cdkModules,
    NgxMaskDirective,
    NgxJsonViewerModule,
    MatMomentDateModule,
    NgxSkeletonLoaderModule.forRoot({ animation: 'progress' }),
    QuillModule.forRoot(),
    MonthYearFormatDirective,
    FormErrorPipe,
    UploadFileComponent,
    ProcessLogComponent,
  ],
  declarations: [
    SidenavComponent,
    SideButtonsComponent,
    UploadImageComponent,
    ValidateTAInputComponent,
    ExpandableButtonComponent,
    ReadOnlyBannerComponent,
    SecondarySideButtonsComponent,
    SimpleTableComponent,
    SearchboxComponent,
    NoDataComponent,
    DialogComponent,
    DialogFormComponent,
    DialogInfoComponent,
    ActionButtonComponent,
    AutocompleteComponent,
    ExpandablePanelComponent,
    SimpleZoomComponent,
    OSQueryFieldComponent,
    InfoHelperComponent,
    MiniatureComponent,
    ConversationMiniatureComponent,
    SmallMiniatureComponent,
    JsonRecordComponent,
    SnackbarComponent,
    BreadcrumbsComponent,
    DatetimeInputComponent,
    DatetimeInputDialogComponent,
    ScrollableTitleComponent,
    ScrollToTopComponent,
    FloatButtonComponent,
    ServerSideAutocompleteComponent,
    DateRangeComponent,
    DateControlComponent,
    ChipsComponent,
    DateDifferencePipe,
    ButtonToggleComponent,
    JsonHighlightPipe,
    ContainInArrayPipe,
    PermissionsPipe,
    SpacesPipe,
    TimeAgoPipe,
    CaseTransformPipe,
    HandleFunctionPipe,
    CustomDatePipe,
    CommentsComponent,
    TimeFrameComponent,
    CommentsListComponent,
    ImageGalleryComponent,
    ImgActionsComponent,
    PhysicalAddressComponent,
    ClientAutocompleteComponent,
    ClientListAutocompleteComponent,
    SplitPaneComponent,
    UnitNanosControlComponent,
    EditorComponent,
    FormErrorsComponent,
    ImgViewerComponent,
    DownloadsManagementComponent,
    ListItemTemplateComponent,
    LongNumberControlComponent,
    MailingListAutocompleteComponent,
    MoneyControlComponent,
    JsonHighlightComponent,
    JsonViewerComponent,
    GalleryViewerComponent,
    SourcesAutocompleteComponent,
    ActivityLogComponent,
    TranslationSwitchComponent,
  ],
  providers: [
    DatePipe,
    TitleCasePipe,
    JsonHighlightPipe,
    PermissionsPipe,
    SpacesPipe,
    DecimalPipe,
    TimeAgoPipe,
    CaseTransformPipe,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
  ],
  exports: [
    ...angularModules,
    ...materialModules,
    NgxMaskDirective,
    SidenavComponent,
    UploadImageComponent,
    ValidateTAInputComponent,
    SideButtonsComponent,
    ExpandableButtonComponent,
    ReadOnlyBannerComponent,
    SecondarySideButtonsComponent,
    SimpleTableComponent,
    SearchboxComponent,
    NoDataComponent,
    DialogComponent,
    DialogFormComponent,
    DialogInfoComponent,
    ActionButtonComponent,
    AutocompleteComponent,
    ExpandablePanelComponent,
    OSQueryFieldComponent,
    InfoHelperComponent,
    MiniatureComponent,
    ConversationMiniatureComponent,
    SmallMiniatureComponent,
    JsonRecordComponent,
    SnackbarComponent,
    BreadcrumbsComponent,
    DatetimeInputComponent,
    DatetimeInputDialogComponent,
    ScrollableTitleComponent,
    ScrollToTopComponent,
    FloatButtonComponent,
    SimpleZoomComponent,
    ServerSideAutocompleteComponent,
    DateRangeComponent,
    DateControlComponent,
    ChipsComponent,
    DateDifferencePipe,
    ButtonToggleComponent,
    JsonHighlightPipe,
    ContainInArrayPipe,
    PermissionsPipe,
    SpacesPipe,
    TimeAgoPipe,
    CaseTransformPipe,
    HandleFunctionPipe,
    CustomDatePipe,
    FormErrorPipe,
    CommentsComponent,
    CommentsListComponent,
    ImageGalleryComponent,
    ImgActionsComponent,
    PhysicalAddressComponent,
    ClientAutocompleteComponent,
    ClientListAutocompleteComponent,
    SplitPaneComponent,
    UnitNanosControlComponent,
    MoneyControlComponent,
    EditorComponent,
    FormErrorsComponent,
    ImgViewerComponent,
    DownloadsManagementComponent,
    ListItemTemplateComponent,
    LongNumberControlComponent,
    MailingListAutocompleteComponent,
    JsonHighlightComponent,
    JsonViewerComponent,
    GalleryViewerComponent,
    SourcesAutocompleteComponent,
    ActivityLogComponent,
    TranslationSwitchComponent,
    QuillModule,
    MonthYearFormatDirective,
    UploadFileComponent,
    ProcessLogComponent,
    TimeFrameComponent,
  ],
})
export class UiModule {}
